import { cur } from '@/lib/currency';
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, MapPin, RefreshCw, AlertTriangle, ArrowLeft, Search } from 'lucide-react';
import api from '@/lib/api';

const COLLECTION_LABELS = {
  sales: 'Sales',
  expenses: 'Expenses',
  supplier_payments: 'Supplier Payments',
  bank_payments: 'Bank Payments',
};

const COLLECTION_COLORS = {
  sales: 'bg-blue-50 text-blue-700 border-blue-200',
  expenses: 'bg-amber-50 text-amber-700 border-amber-200',
  supplier_payments: 'bg-purple-50 text-purple-700 border-purple-200',
  bank_payments: 'bg-teal-50 text-teal-700 border-teal-200',
};

const fmtSR = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10);
  return d || '—';
};

export default function ReconcileUnassignedPage() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState(new Set()); // Set of "coll::id"
  const [targetBranch, setTargetBranch] = useState(() => new URLSearchParams(window.location.search).get('branch') || '');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [resData, resBranches] = await Promise.all([
        api.get('/branches/unassigned-entries', { params: { limit: 500 } }),
        api.get('/branches'),
      ]);
      setData(resData.data);
      const bs = Array.isArray(resBranches.data) ? resBranches.data : (resBranches.data?.data || []);
      setBranches(bs);
      if (!targetBranch && bs.length > 0) setTargetBranch(bs[0].id);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed to load unassigned entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Flatten rows for the active tab + apply search.
  const flatRows = useMemo(() => {
    if (!data?.items) return [];
    const collections = activeTab === 'all' ? Object.keys(data.items) : [activeTab];
    let rows = [];
    for (const coll of collections) {
      (data.items[coll] || []).forEach(r => rows.push({ ...r, _coll: coll, _key: `${coll}::${r.id}` }));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r => {
        const blob = `${r.label || ''} ${r.category || ''} ${r.supplier_name || ''} ${r.vendor || ''} ${r.customer_name || ''} ${r.reference || ''} ${r.notes || ''} ${r.payment_mode || ''}`.toLowerCase();
        return blob.includes(q) || String(r.amount).includes(q);
      });
    }
    return rows.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [data, activeTab, search]);

  const totalSelectedAmount = useMemo(() => {
    if (!data?.items) return 0;
    let tot = 0;
    for (const coll of Object.keys(data.items)) {
      for (const r of data.items[coll]) {
        if (selected.has(`${coll}::${r.id}`)) tot += Number(r.amount || 0);
      }
    }
    return tot;
  }, [selected, data]);

  const toggleOne = (key) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAllVisible = () => {
    if (flatRows.every(r => selected.has(r._key)) && flatRows.length > 0) {
      // De-select all currently visible
      setSelected(s => {
        const next = new Set(s);
        flatRows.forEach(r => next.delete(r._key));
        return next;
      });
    } else {
      setSelected(s => {
        const next = new Set(s);
        flatRows.forEach(r => next.add(r._key));
        return next;
      });
    }
  };

  const handleAssign = async () => {
    if (selected.size === 0) { toast.error('Pick at least one row'); return; }
    if (!targetBranch) { toast.error('Pick a target branch'); return; }
    const branchName = branches.find(b => b.id === targetBranch)?.name || targetBranch;
    if (!window.confirm(`Assign ${selected.size} entries (${fmtSR(totalSelectedAmount)}) to "${branchName}"? This rewrites the branch_id on each record.`)) return;

    setBusy(true);
    try {
      const entries = Array.from(selected).map(k => {
        const [coll, id] = k.split('::');
        return { collection: coll, id };
      });
      const res = await api.post('/branches/bulk-assign-unassigned', { branch_id: targetBranch, entries });
      toast.success(`Assigned ${res.data?.total_updated || 0} entries to ${res.data?.branch_name || branchName}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Bulk assign failed');
    } finally {
      setBusy(false);
    }
  };

  const counts = data?.counts || {};

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-[1400px] mx-auto" data-testid="reconcile-unassigned-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/branch-summary" className="text-muted-foreground hover:text-foreground" data-testid="back-to-branch-summary">
              <ArrowLeft size={20} />
            </Link>
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-rose-200/50">
              <MapPin size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Reconcile Unassigned Branch</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Find every record saved without a branch and assign them in bulk.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="reconcile-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin mr-1.5' : 'mr-1.5'} />
            Refresh
          </Button>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <SummaryTile
            label="Total Unassigned"
            value={data?.total ?? '—'}
            tone="rose"
            active={activeTab === 'all'}
            onClick={() => setActiveTab('all')}
            testid="tab-all"
          />
          {Object.keys(COLLECTION_LABELS).map(coll => (
            <SummaryTile
              key={coll}
              label={COLLECTION_LABELS[coll]}
              value={counts[coll] ?? '—'}
              tone="slate"
              active={activeTab === coll}
              onClick={() => setActiveTab(coll)}
              testid={`tab-${coll}`}
            />
          ))}
        </div>

        {/* Toolbar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search label / category / supplier / amount…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="reconcile-search"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Assign selected to:</span>
                <Select value={targetBranch} onValueChange={setTargetBranch}>
                  <SelectTrigger className="w-[200px]" data-testid="reconcile-target-branch">
                    <SelectValue placeholder="Pick a branch…" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleAssign}
                  disabled={busy || selected.size === 0 || !targetBranch}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="reconcile-assign-btn"
                >
                  {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                  Assign {selected.size > 0 ? `${selected.size} entries` : ''}
                </Button>
              </div>
            </div>
            {selected.size > 0 && (
              <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2" data-testid="reconcile-selection-bar">
                <Badge variant="outline">{selected.size} selected</Badge>
                <span>·</span>
                <span>Total amount: <strong className="text-foreground">{fmtSR(totalSelectedAmount)}</strong></span>
                <button className="ml-2 text-info hover:underline" onClick={() => setSelected(new Set())} data-testid="reconcile-clear-selection">
                  Clear
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center text-muted-foreground">
                <Loader2 size={20} className="animate-spin inline mr-2" /> Loading…
              </div>
            ) : flatRows.length === 0 ? (
              <div className="p-12 text-center">
                <div className="inline-flex w-12 h-12 rounded-full bg-success/10 items-center justify-center mb-3">
                  <MapPin size={20} className="text-success" />
                </div>
                <div className="font-semibold text-foreground">Nothing to reconcile</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {data?.total === 0
                    ? 'Every record has a branch assigned. 🎉'
                    : 'No entries match your search in the current tab.'}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reconcile-table">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left p-3 w-10">
                        <input
                          type="checkbox"
                          checked={flatRows.length > 0 && flatRows.every(r => selected.has(r._key))}
                          onChange={toggleAllVisible}
                          data-testid="reconcile-select-all"
                        />
                      </th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Description</th>
                      <th className="text-left p-3">Mode</th>
                      <th className="text-right p-3">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {flatRows.slice(0, 1000).map(r => {
                      const checked = selected.has(r._key);
                      return (
                        <tr
                          key={r._key}
                          className={`hover:bg-muted/20 cursor-pointer ${checked ? 'bg-rose-50/40' : ''}`}
                          onClick={() => toggleOne(r._key)}
                          data-testid={`reconcile-row-${r._key}`}
                        >
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleOne(r._key)}
                              data-testid={`reconcile-check-${r._key}`}
                            />
                          </td>
                          <td className="p-3 font-mono text-xs">{fmtDate(r.date)}</td>
                          <td className="p-3">
                            <span className={`text-[10px] px-2 py-0.5 rounded-md border ${COLLECTION_COLORS[r._coll]}`}>
                              {COLLECTION_LABELS[r._coll]}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="font-medium truncate max-w-[420px]">
                              {r.label || r.category || r.supplier_name || r.customer_name || r.reference || '—'}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {[r.category, r.supplier_name, r.customer_name, r.vendor, r.notes].filter(Boolean).join(' · ')}
                            </div>
                          </td>
                          <td className="p-3 text-xs capitalize">{r.payment_mode || '—'}</td>
                          <td className="p-3 text-right font-mono">{fmtSR(r.amount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {flatRows.length > 1000 && (
              <div className="p-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-800 flex items-center gap-2">
                <AlertTriangle size={14} />
                Showing first 1,000 of {flatRows.length} rows. Narrow your search or switch tabs to see the rest.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function SummaryTile({ label, value, tone, active, onClick, testid }) {
  const toneClass = tone === 'rose'
    ? (active ? 'bg-rose-600 text-white shadow-lg' : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200')
    : (active ? 'bg-stone-900 text-white shadow-lg' : 'bg-card text-foreground hover:bg-muted border border-border');
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl p-3 text-left transition-all ${toneClass}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </button>
  );
}
